import React, { useState, useEffect, useRef, useCallback } from 'react'
import MainLayout from '../layouts/mainLayout'
import {
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
    CircularProgress,
} from '@material-ui/core'
import { green } from '@material-ui/core/colors'
import InputTypes from '../constants/editabletypes.constants'
import reviewTypeConstants from '../constants/reviewType.constants'
import letterEditableByConstants from '../constants/letterEditableBy.constants'
import { makeStyles } from '@material-ui/core/styles'
import {
    makeEditableInvalid,
    makeEditableValid,
    makeEditableOptional,
    createNewPage,
    getOverflowContent,
    getDocumentHTML,
    addEditableClickHandlers,
    appendPage,
    getHtmlForEditableId,
    removeNonMandatoryFieldsOnCompletion,
} from '../utils/domHelpers'
import wizardService from '../services/wizard.service'
import { useHistory, useLocation } from 'react-router-dom'
import ScrollToTop from '../components/navigation/ScrollToTop'
import { useNavigation } from '../contexts/navigationContext'
import mergeTypes from '../constants/mergetypes.constants'
import { useDispatch, useSelector } from 'react-redux'
import { enqueueSnackbar } from '../actions/notification.actions'
import queryString from 'query-string'
import HtmlDialog from '../components/editableDialogs/htmlDialog'
import DatePickerDialog from '../components/editableDialogs/datePickerDialog'
import TimePickerDialog from '../components/editableDialogs/timePickerDialogAccessible'
import DropdownDialog from '../components/editableDialogs/dropdownDialog'
import FreetypeDialog from '../components/editableDialogs/freetypeDialog'
import CarouselDialog from '../components/editableDialogs/carouselDialog'
import rolesConstants from '../constants/roles.constants'
import tokenConstants from '../constants/token.constants'
import { DocumentStatus } from '../utils/documentHelper'
import LoadingPage from '../components/loadingPage'
import { downloadFile } from '../utils/file'
import editabletypesConstants from '../constants/editabletypes.constants'
import { dateTimeConstants } from '../constants/datetime.constants'
import caseService from '../services/case.service'
import { getUserRoles } from '../helpers/user'
import { participantRole } from '../constants/participantRole.constants'
import { convertHtmlIntoHumanText } from '../helpers/screenReader'
import WizardNavButtons from '../components/WizardNavButtons'
import { formatDate } from '../helpers/dateFns'
import { IsFeatureFlagEnabled } from '../providers/globalFeatureFlag.provider'
import { GlobalFeatureFlagConstants } from '../constants/globalFeatureFlag.constants'
import {
    calculateDocumentProgress,
    isEditableComplete,
} from '../utils/wizardProgressCalculator'

const useStyles = makeStyles(theme => ({
    pages: {
        padding: '24px',
    },
    previewDisclaimer: {
        color: '#a1a1a1',
        textAlign: 'center',
        margin: 0,
    },

    contentContainer: {
        paddingTop: '16px',
    },
    unsavedChangesChip: {
        color: theme.palette.getContrastText(theme.palette.navigation.main),
        fontWeight: 'bold',
    },
    modalSaveButtonProgress: {
        color: green[500],
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -12,
        marginLeft: -12,
    },
    modalSaveButtonWrapper: {
        position: 'relative',
    },
    hide: {
        display: 'none',
    },
}))

function Wizard() {
    const classes = useStyles()
    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation()
    const { returnToUrl } = useNavigation()
    const [progress, setProgress] = useState(0)
    const pagesRef = useRef(null)
    const [editableLookups, setEditableLookups] = useState({})
    const [htmlEditorState, setHtmlEditorState] = useState({})
    const [carouselState, setCarouselState] = useState({})
    const [documentData, setDocumentData] = useState({})
    const [mergefieldData, setMergefieldData] = useState({})
    const [showEditableDialog, setShowEditableDialog] = useState(false)
    const [selectedEditable, setSelectedEditable] = useState(null)
    const [unsavedChanges, setUnsavedChanges] = useState(false)
    const [managerMadeChanges, setManagerMadeChanges] = useState(false)
    const [isConfirmExitModalOpen, setIsConfirmExitModalOpen] = useState(false)
    const [lastAutoSavedDate, setLastAutoSavedDate] = useState(null)
    const [isSaving, setIsSaving] = useState(false)
    const [isCompleting, setIsCompleting] = useState(false)
    const [isDownloading, setIsDownloading] = useState(false)
    const [openReviewModal, setOpenReviewModal] = useState(false)
    const { user } = useSelector(state => state.auth)
    const [documentStatus, setDocumentStatus] = useState(null)
    const [prepopulatedCompleted, setPrepopulatedCompleted] = useState(false)
    const countdownTimerRef = useRef(null)
    const [reviewType, setReviewType] = useState(
        reviewTypeConstants.AdviserReview
    )
    const [letterEditableBy, setLetterEditableBy] = useState(
        letterEditableByConstants.No
    )
    const [isEditing, setIsEditing] = useState(false)
    const [editableContent, setEditableContent] = useState('')
    const [
        completionConfirmationModal,
        setCompletionConfirmationModal,
    ] = useState(false)
    const autoSaveNotesAndLettersInSeconds = useSelector(
        state => state.service?.autoSaveNotesAndLettersInSeconds ?? 0
    )
    const [previouslyFocusedElement, setPreviouslyFocusedElement] = useState(
        false
    )
    const isDocDirectRoute = location.pathname.includes(
        '/wizard/docServiceUser'
    )

    const isBusinessUser = user.profile[tokenConstants.ROLES].includes(
        rolesConstants.BUSINESS
    )
    const isAdviser = user.profile[tokenConstants.ROLES].includes(
        rolesConstants.HR
    )

    const isDocServiceUser = user.profile[tokenConstants.ROLES].includes(
        rolesConstants.DOC_SERVICE_USER
    )

    const hasHrUserRole = user.profile[tokenConstants.ROLES].includes(
        rolesConstants.HR
    )
    const tempSavedDateRef = useRef(null)
    const htmlEditorStateRef = useRef(null)
    const isManager = !isAdviser && (isBusinessUser || isDocServiceUser)
    const [caseIdForDocServiceuser, setCaseIdForDocServiceuser] = useState(null)

    const documentInReview = documentStatus === DocumentStatus.AdviserReview
    const documentReviewEnded = documentStatus === DocumentStatus.ReviewEnded
    const documentCompleted = documentStatus === DocumentStatus.Completed
    const documentInProgress = documentStatus === DocumentStatus.InProgress
    const creatingNewDocument = location.pathname.includes('new')

    const defaultDisplayDateFormat = dateTimeConstants.DEFAULT_DATE_FORMAT
    const defaultDisplayTimeFormat = dateTimeConstants.DEFAULT_TIME_FORMAT
    const [titleAnnouncement, setTitleAnnouncement] = useState('')
    const [bodyAnnouncement, setBodyAnnouncement] = useState('')

    const isCurrentDateCompleteFeatureFlagEnabled = IsFeatureFlagEnabled(
        GlobalFeatureFlagConstants.CurrentDateComplete_53966
    )

    const isTimePickerResetAndFormatFlagEnabled = IsFeatureFlagEnabled(
        GlobalFeatureFlagConstants.TimePickerResetAndFormat_77825
    )

    const userCanEditFullDocument = useCallback(() => {
        if (documentCompleted) {
            return false
        }
        return (
            (letterEditableBy === letterEditableByConstants.HRUsers &&
                isAdviser) ||
            (letterEditableBy ===
                letterEditableByConstants.HRAndBusinessUsers &&
                (isAdviser || isBusinessUser || isDocServiceUser)) ||
            (letterEditableBy === letterEditableByConstants.BusinessUsers &&
                (isDocServiceUser || isBusinessUser))
        )
    })

    const minimumAutoSaveInSeconds = 15

    const parsedQuery = queryString.parse(location.search)

    const { templateId, caseId } = parsedQuery

    // Auto save only if there is a change and also the 'autoSaveNotesAndLettersInSeconds' is greater than 15 seconds
    useEffect(() => {
        if (
            unsavedChanges &&
            autoSaveNotesAndLettersInSeconds >= minimumAutoSaveInSeconds
        ) {
            const autoSaveInterval = setInterval(() => {
                saveDocument({ returnAfterSave: true, autoSaveDocument: true })
            }, autoSaveNotesAndLettersInSeconds * 1000)

            return () => {
                clearInterval(autoSaveInterval)
                setUnsavedChanges(false)
            }
        }
    }, [autoSaveNotesAndLettersInSeconds, saveDocument, unsavedChanges])

    useEffect(() => {
        if (documentData?.documentName) {
            setTitleAnnouncement('')
            setTimeout(() => {
                setTitleAnnouncement(`Title: ${documentData.documentName}.`)
            }, 100)
        }
    }, [documentData?.documentName])

    useEffect(() => {
        if (documentData?.contentHtml) {
            setBodyAnnouncement('')
            setTimeout(() => {
                const srText = convertHtmlIntoHumanText(
                    documentData?.contentHtml
                )
                setBodyAnnouncement(`Body: ${srText}.`)
            }, 100)
        }
    }, [documentData?.contentHtml])

    const withUnsavedChanges = callback => {
        setManagerMadeChanges(
            (reviewType === reviewTypeConstants.AdviserReview ||
                reviewType === reviewTypeConstants.AdviserCompletion) &&
                isManager
        )

        if (documentInProgress && !documentInReview) {
            setDocumentStatus(DocumentStatus.InProgress)
        }
        if (!unsavedChanges) {
            setUnsavedChanges(true)
        }

        return callback
    }

    const getAllEditables = () => {
        if (!pagesRef.current) return []
        return Array.from(pagesRef.current.querySelectorAll('[data-tag-id]'))
    }

    const processPage = useCallback(
        ({ page, header, footer }) => {
            const overflowContent = getOverflowContent(page)

            if (overflowContent.length <= 0) return

            const newPage = createNewPage({
                content: overflowContent,
                header: header,
                footer: footer,
                fontSize: documentData.fontSize,
                fontFamily: documentData.fontFamily,
            })
            appendPage(pagesRef.current, newPage)

            processPage({ page: newPage, header, footer })
        },
        [documentData]
    )

    const selectEditable = useCallback(e => {
        const { target } = e

        if (!Element.prototype.matches) {
            Element.prototype.matches =
                Element.prototype.msMatchesSelector ||
                Element.prototype.webkitMatchesSelector
        }

        if (!Element.prototype.closest) {
            Element.prototype.closest = function (s) {
                var el = this

                do {
                    if (Element.prototype.matches.call(el, s)) return el
                    el = el.parentElement || el.parentNode
                } while (el !== null && el.nodeType === 1)
                return null
            }
        }

        const closest = target.closest('[data-tag-id]')
        if (closest) {
            const id = closest.getAttribute('data-tag-id')
            const bounds = closest.getBoundingClientRect()

            if (editableLookups[id]) {
                setSelectedEditable({
                    id,
                    label: editableLookups[id].name,
                    description: editableLookups[id].description,
                    inputTypeId: editableLookups[id].inputTypeId,
                    options: editableLookups[id].options,
                    coords: { x: bounds.left, y: bounds.top },
                    DOMElement: closest,
                    isMandatory: editableLookups[id].isMandatory,
                    inputCustomFormat: editableLookups[id]?.inputCustomFormat,
                })
                setShowEditableDialog(true)
            }
        }
    })

    const deselectEditable = () => {
        setSelectedEditable(null)
        setShowEditableDialog(false)
        refreshPages()
    }

    const focusOnTabIndex = tabIndexValue => {
        const element = document.querySelector(`[tabindex="${tabIndexValue}"]`)
        if (element) {
            element.focus()
        }
    }

    const setupPages = useCallback(
        ({ content, header, footer }) => {
            const startingPage = createNewPage({
                isFirstPage: true,
                content: content,
                header: header,
                footer: footer,
                fontSize: documentData.fontSize,
                fontFamily: documentData.fontFamily,
            })
            appendPage(pagesRef.current, startingPage)
            processPage({ page: startingPage, header, footer })
        },
        [processPage, documentData]
    )

    const getDocumentDataFromApiAsync = async () => {
        const parsedQuery = queryString.parse(location.search)
        let {
            existingDocumentGuid,
            documentGuid,
            templateId,
            caseId,
            fromAddressUserId,
            code,
            workflowTrackingRecordId,
        } = parsedQuery

        let errorMessage = null

        if (code && isDocDirectRoute) {
            const response = await wizardService.checkDocDirectCode(code)
            if (!response.isError) {
                documentGuid = response.documentGuid
                setUnsavedChanges(true)
                setCaseIdForDocServiceuser(response.caseTrackingRecordId)
            }
            errorMessage = 'You dont have access to this document'
        }

        // If the user is on "docServiceUser" route, we treat it as an existing document
        const resolvedExistingDocumentGuid = isDocDirectRoute
            ? documentGuid
            : existingDocumentGuid
        const isCreatingNewDocument = !resolvedExistingDocumentGuid

        const hasMissingParametersForNewDocument =
            isCreatingNewDocument && (!documentGuid || !templateId)

        if (isCreatingNewDocument && hasMissingParametersForNewDocument) {
            history.push('/oops')
            dispatch(
                enqueueSnackbar({
                    message:
                        errorMessage !== null
                            ? errorMessage
                            : 'Document request missing required parameters',
                    options: { variant: 'error' },
                })
            )
            return
        }
        try {
            let response
            if (isCreatingNewDocument) {
                response = await wizardService.createDocument({
                    documentTemplateId: templateId,
                    documentGuid,
                    caseId,
                    fromAddressUserId,
                    workflowTrackingRecordId,
                })
            } else {
                response = await wizardService.continueDocument({
                    existingDocumentGuid: resolvedExistingDocumentGuid,
                })
            }

            if (!response) throw new Error('Invalid response from API')

            setMergefieldData(response)
            setDocumentData(response.documentData)
            setLastAutoSavedDate(
                response.documentData.documentVersionLastSavedDate
            )
        } catch (error) {
            console.error(error)
            history.push('/oops')
            dispatch(
                enqueueSnackbar({
                    message: 'Could not load document. Please try again.',
                    options: { variant: 'error' },
                })
            )
        }
    }

    const pagesRefCallback = useCallback(node => {
        if (node == null) return

        if (pagesRef.current && pagesRef.current !== node) {
            pagesRef.current.innerHTML = ''
        }

        pagesRef.current = node
    }, [])

    const [isContentInitialized, setIsContentInitialized] = useState(false)

    useEffect(() => {
        if (!Object.keys(documentData).length || !pagesRef.current) {
            return
        }

        // Only initialize content once
        if (isContentInitialized) {
            return
        }

        // Initialize content and editables
        const { contentHtml = '', branding = {} } = documentData
        const { headerHtml = '', footerHtml = '' } = branding

        setupPages({
            content: contentHtml,
            header: headerHtml,
            footer: footerHtml,
        })
        setupEditables()

        setIsDocumentVisibleToBusinessUser(
            documentData.isDocumentVisibleToBusinessUsers
        )
        // Mark content as initialized to prevent re-initialization
        setIsContentInitialized(true)
    }, [documentData, isContentInitialized, setupEditables, setupPages])

    const auth = useSelector(state => state.auth.profile)
    const service = useSelector(state => state.service)
    const [
        isDocumentVisibleToBusinessUser,
        setIsDocumentVisibleToBusinessUser,
    ] = useState(false)
    const [caseParticipants, setCaseParticipants] = useState([])
    const [
        isDisplaySendToManagerButton,
        setIsDisplaySendToManagerButton,
    ] = useState(false)

    const userRoles = getUserRoles(auth)
    const isHrUser = userRoles.includes('HRUser')

    const isRemoteLetterAccess =
        service?.isAllowRemoteLetterForManagerEnabled || false

    useEffect(() => {
        if (!documentData?.contentHtml) {
            ;(async function setupDocumentDataFromApiAsync() {
                await getDocumentDataFromApiAsync().then(response => {
                    if (response && response.documentData) {
                        setMergefieldData(response)
                        setDocumentData(response.documentData)
                        setLastAutoSavedDate(
                            response.documentData.documentVersionLastSavedDate
                        )
                    }
                })
            })()
        }
    }, [documentData.contentHtml]) // Fetch data on mount

    useEffect(() => {
        const fetchCaseParticipants = async () => {
            if (caseId) {
                const response = await caseService.getActiveCaseParticipantsWithRoles(
                    caseId
                )

                if (response.isError) {
                    console.error('Error when fetching participants')
                    // add toast or something
                    return
                }

                const excludedRoles = [
                    participantRole.OBSERVER_ID,
                    participantRole.SILENT_OBSERVER_ID,
                ]

                setCaseParticipants(prev => ({
                    ...prev,
                    activeCaseParticipants: response.data.filter(
                        c => !excludedRoles.includes(c.participantRoleLookupId)
                    ),
                }))
            }
        }
        fetchCaseParticipants()
    }, [caseId])

    useEffect(() => {
        setIsDisplaySendToManagerButton(
            isRemoteLetterAccess && isHrUser && isDocumentVisibleToBusinessUser
        )
    }, [isRemoteLetterAccess, isHrUser, isDocumentVisibleToBusinessUser])

    const removeEditableAttributes = htmlContent => {
        const parser = new DOMParser()
        const doc = parser.parseFromString(htmlContent, 'text/html')

        doc.querySelectorAll('[data-tag-id], [is-mandatory]').forEach(
            element => {
                element.removeAttribute('data-tag-id')
                element.removeAttribute('is-mandatory')
            }
        )

        doc.querySelectorAll('.editable').forEach(element => {
            element.classList.remove('editable')
        })

        doc.querySelectorAll('.invalid').forEach(element => {
            element.classList.remove('invalid')
        })

        // Return the cleaned HTML
        return doc.body.innerHTML
    }

    const setupEditables = () => {
        const editableDetails = {}
        const editableHtmlEditorState = {}
        const creatingNewDocument = location.pathname.includes('new')

        setDocumentStatus(documentData.status)
        setReviewType(documentData.reviewType)
        setLetterEditableBy(documentData.letterEditableBy)

        const allEditables = getAllEditables()

        documentData.editableRegions.forEach(editableRegion => {
            const { editableRegionId, ...rest } = editableRegion
            editableDetails[editableRegionId] = {
                ...rest,
            }

            // set up html editor state
            if (editableRegion.inputTypeId === InputTypes.HtmlEditor) {
                editableHtmlEditorState[editableRegionId] =
                    creatingNewDocument && editableRegion.prePopulatedState
                        ? editableRegion.prePopulatedState
                        : getHtmlForEditableId(
                              allEditables,
                              editableRegionId,
                              editableRegion.name
                          )
            }
        })

        setEditableLookups(editableDetails)
        setHtmlEditorState(editableHtmlEditorState)
    }

    const calculateProgress = useCallback(() => {
        const allEditables = getAllEditables()
        const progressPercentage = calculateDocumentProgress(
            allEditables,
            editableLookups,
            userCanEditFullDocument()
        )
        setProgress(progressPercentage)
    }, [editableLookups, userCanEditFullDocument])

    // Set is-mandatory attribute on all editables whenever editableLookups changes
    // This ensures makeEditableInvalid works correctly for both new and existing documents
    useEffect(() => {
        if (!Object.keys(editableLookups).length) return

        const allEditables = getAllEditables()
        allEditables.forEach(editable => {
            const editableId = editable.getAttribute('data-tag-id')
            const editableLookup = editableLookups[editableId]
            if (editableLookup) {
                editable.setAttribute(
                    'is-mandatory',
                    editableLookup.isMandatory
                )
            }
        })

        addEditableClickHandlers(allEditables, editableLookups, selectEditable)

        calculateProgress()
    }, [editableLookups])

    const editableIsCompleteById = useCallback(
        id => {
            const allEditables = getAllEditables()
            const editable = allEditables.find(
                x => x.getAttribute('data-tag-id') === id
            )
            const editableLookup = editableLookups[id]
            return isEditableComplete(editable, editableLookup)
        },
        [editableLookups]
    )

    // pre-populate editable values

    // pre-populate editable values
    useEffect(() => {
        if (!creatingNewDocument || prepopulatedCompleted) {
            return
        }

        const editableList = getAllEditables()

        editableList.forEach(editable => {
            const editableId = editable.getAttribute('data-tag-id')
            const editableLookup = editableLookups[editableId]

            if (!editableLookup) {
                return
            } else setPrepopulatedCompleted(true)
            const editableMergeData =
                editableLookup && editableLookup.editableMergeField
            if (!editableLookup.isMandatory) {
                makeEditableOptional(editable)
            }

            // merge data depending on merge type
            if (mergefieldData && editableMergeData) {
                let mergedValue
                let isEventDateValue = false
                switch (editableMergeData.type) {
                    case mergeTypes.caseData:
                        mergedValue =
                            (mergefieldData &&
                                mergefieldData.caseData &&
                                mergefieldData.caseData[
                                    editableMergeData.value
                                ]) ||
                            ''
                        break
                    case mergeTypes.employeeData:
                        mergedValue =
                            (mergefieldData &&
                                mergefieldData.employeeData &&
                                mergefieldData.employeeData[
                                    editableMergeData.value
                                ]) ||
                            ''
                        break
                    case mergeTypes.officeAddress:
                        mergedValue =
                            (mergefieldData &&
                                mergefieldData.officeAddressData &&
                                mergefieldData.officeAddressData[
                                    editableMergeData.value
                                ]) ||
                            ''
                        break
                    case mergeTypes.caseEventData:
                        isEventDateValue = true
                        const eventData = editableLookups[editableId]?.event

                        mergedValue = formatDate(
                            editableLookup?.inputTypeId,
                            eventData?.eventOriginalDateValue,
                            editableLookups[editableId]?.inputCustomFormat
                        )

                        break
                    default:
                        mergedValue = editableLookups[editableId].name
                }

                // if merged value doesnt exist, set to editable name
                if (!mergedValue) {
                    editable.innerText = editableLookups[editableId].name
                    makeEditableInvalid(editable)
                    return
                }

                if (!isEventDateValue) {
                    let format

                    if (/date/i.test(editableMergeData.value)) {
                        format =
                            editableLookup.inputCustomFormat ??
                            defaultDisplayDateFormat
                    } else if (/time/i.test(editableMergeData.value)) {
                        format =
                            editableLookup.inputCustomFormat ??
                            defaultDisplayTimeFormat
                    }

                    if (format) {
                        mergedValue = formatDate(
                            editableLookup.inputTypeId,
                            mergedValue,
                            format
                        )
                    }
                }

                editable.innerText = mergedValue

                makeEditableValid(editable)
            }
        })
        calculateProgress()
        addEditableClickHandlers(
            getAllEditables(),
            editableLookups,
            selectEditable
        )
    }, [
        mergefieldData,
        editableLookups,
        calculateProgress,
        creatingNewDocument,
    ])

    const updateDropdownValue = (value, isSilentSaving = false) => {
        if (!value) {
            resetDropdownValue()
            return
        }

        selectedEditable.DOMElement.innerText = value
        makeEditableValid(selectedEditable.DOMElement)

        if (!isSilentSaving) {
            deselectEditable()
        }
    }

    const resetDropdownValue = () => {
        selectedEditable.DOMElement.innerText = selectedEditable.label
        makeEditableInvalid(selectedEditable.DOMElement)
        deselectEditable()
    }

    const updateFreetypeValue = (value, isSilentSaving = false) => {
        if (value === selectedEditable.label) {
            resetFreetypeValue()
            return
        }

        selectedEditable.DOMElement.innerText = value

        const hasNoValue = !(value?.trim().length > 0)

        const isComplete = editableIsCompleteById(selectedEditable.id)

        if ((hasNoValue || !isComplete) && selectedEditable.isMandatory) {
            makeEditableInvalid(selectedEditable.DOMElement)
        } else {
            makeEditableValid(selectedEditable.DOMElement)
        }

        if (!isSilentSaving) {
            deselectEditable()
        }
    }

    const resetFreetypeValue = () => {
        selectedEditable.DOMElement.innerText = selectedEditable.label
        makeEditableInvalid(selectedEditable.DOMElement)
        deselectEditable()
    }

    const updateDateValue = (date, isSilentSaving = false) => {
        const formatted = formatDate(
            InputTypes.DatePicker,
            date,
            selectedEditable?.inputCustomFormat ?? defaultDisplayDateFormat
        )

        selectedEditable.DOMElement.innerText = formatted
        makeEditableValid(selectedEditable.DOMElement)
        if (!isSilentSaving) {
            deselectEditable()
        }
    }

    const resetDateValue = () => {
        selectedEditable.DOMElement.innerText = selectedEditable.label
        makeEditableInvalid(selectedEditable.DOMElement)
        deselectEditable()
    }

    const updateTimeValue = (time, isSilentSaving = false) => {
        const formatted = formatDate(
            InputTypes.TimePicker,
            time,
            selectedEditable?.inputCustomFormat ?? defaultDisplayTimeFormat
        )
        selectedEditable.DOMElement.innerText = formatted
        makeEditableValid(selectedEditable.DOMElement)
        if (!isSilentSaving) {
            deselectEditable()
        }
    }

    const resetTimeValue = () => {
        selectedEditable.DOMElement.innerText = selectedEditable.label
        makeEditableInvalid(selectedEditable.DOMElement)
        deselectEditable()
    }

    const updateHtmlEditorState = (html, isSilentSaving = false) => {
        htmlEditorStateRef.current = {
            ...htmlEditorState,
            [selectedEditable.id]: html,
        }

        selectedEditable.DOMElement.innerHTML = html
        const isComplete = editableIsCompleteById(selectedEditable.id)
        if (isComplete) {
            makeEditableValid(selectedEditable.DOMElement)
        } else if (!selectedEditable.isMandatory) {
            makeEditableValid(selectedEditable.DOMElement)
        }

        if (!isSilentSaving) {
            deselectEditable()
        }
    }

    const resetHtmlEditorState = () => {
        const prePopulatedState =
            editableLookups[selectedEditable.id].prePopulatedState
        const editableType = editableLookups[selectedEditable.id].inputTypeId

        var defaultValue =
            prePopulatedState || `<p>${selectedEditable.label}</p>`

        if (editableType == editabletypesConstants.HtmlEditor) {
            //this sets pre-pop text
            setHtmlEditorState({
                ...htmlEditorState,
                [selectedEditable.id]: prePopulatedState || '',
            })
        } else {
            setHtmlEditorState({
                ...htmlEditorState,
                [selectedEditable.id]: defaultValue,
            })
        }

        // this sets on screen text on letter
        selectedEditable.DOMElement.innerHTML = defaultValue
        makeEditableInvalid(selectedEditable.DOMElement)
        deselectEditable()
    }

    const updateCarouselValue = (
        selectedId,
        selectedHtml,
        isSilentSaving = false
    ) => {
        setCarouselState({
            ...carouselState,
            [selectedEditable.id]: selectedId,
        })
        selectedEditable.DOMElement.innerHTML = selectedHtml
        makeEditableValid(selectedEditable.DOMElement)
        if (!isSilentSaving) {
            deselectEditable()
        }
    }

    const resetCarouselValue = () => {
        selectedEditable.DOMElement.innerHTML = `${selectedEditable.label}`
        setCarouselState({ ...carouselState, [selectedEditable.id]: 0 })
        makeEditableInvalid(selectedEditable.DOMElement)
        deselectEditable()
    }

    // calculate progress and save state
    useEffect(() => {
        if (showEditableDialog) {
            setPreviouslyFocusedElement(document.activeElement?.tabIndex || -1)
            setUnsavedChanges(false)
        } else {
            clearCountdown()
            calculateProgress()

            // Update the states only when the dialog is closed
            if (tempSavedDateRef.current) {
                setLastAutoSavedDate(tempSavedDateRef.current)
                tempSavedDateRef.current = null
            }

            if (htmlEditorStateRef.current) {
                setHtmlEditorState(htmlEditorStateRef.current)
                htmlEditorStateRef.current = null
            }

            if (previouslyFocusedElement) {
                setTimeout(() => {
                    focusOnTabIndex(previouslyFocusedElement)
                }, 100) // Delay to allow page to render before setting the focus
            }
        }
    }, [
        editableLookups,
        showEditableDialog,
        calculateProgress,
        previouslyFocusedElement,
    ])

    const refreshPages = () => {
        const { branding = { headerHtml: '', footerHtml: '' } } = documentData
        const pagesContainer = document.getElementById('pages')
        const originalHtml = getDocumentHTML(pagesContainer)

        pagesContainer.innerHTML = ''
        setupPages({
            content: originalHtml,
            header: branding.headerHtml,
            footer: branding.footerHtml,
        })
        addEditableClickHandlers(
            getAllEditables(),
            editableLookups,
            selectEditable
        )
    }

    const clearCountdown = () => {
        if (countdownTimerRef.current) {
            clearTimeout(countdownTimerRef.current)
            countdownTimerRef.current = null
        }
    }

    const handleChange = async (e, method) => {
        // Clear the previous countdown before starting a new one
        clearCountdown()

        if (autoSaveNotesAndLettersInSeconds >= minimumAutoSaveInSeconds) {
            try {
                const timeIsUp = await new Promise(resolve => {
                    countdownTimerRef.current = setTimeout(() => {
                        resolve(true)
                    }, minimumAutoSaveInSeconds * 1000)
                })

                if (timeIsUp) {
                    method(e, true)
                    await silentSaving()
                    clearCountdown()
                }
            } catch (error) {
                console.error('Error during countdown or save:', error)
                clearCountdown()
            }
        }
    }

    const PopoutComponent = () => {
        switch (selectedEditable.inputTypeId) {
            case InputTypes.FreeText:
                return (
                    <FreetypeDialog
                        title={selectedEditable.label}
                        description={selectedEditable.description}
                        open={showEditableDialog}
                        onConfirm={e =>
                            withUnsavedChanges(updateFreetypeValue(e))
                        }
                        onReset={() => withUnsavedChanges(resetFreetypeValue())}
                        onClose={deselectEditable}
                        onChange={e => handleChange(e, updateFreetypeValue)}
                        currentValue={selectedEditable.DOMElement.innerText}
                    />
                )
            case InputTypes.DatePicker:
                const currentDate =
                    isCurrentDateCompleteFeatureFlagEnabled &&
                    !editableIsCompleteById(selectedEditable.id)
                        ? null
                        : selectedEditable.DOMElement.innerText
                return (
                    <DatePickerDialog
                        title={selectedEditable.label}
                        description={selectedEditable.description}
                        open={showEditableDialog}
                        onConfirm={e => withUnsavedChanges(updateDateValue(e))}
                        onReset={() => withUnsavedChanges(resetDateValue())}
                        onClose={deselectEditable}
                        onChange={e => {
                            handleChange(e, updateDateValue)
                        }}
                        currentDate={currentDate}
                        dateFormat={
                            selectedEditable?.inputCustomFormat ??
                            defaultDisplayDateFormat
                        }
                    />
                )
            case InputTypes.TimePicker:
                return (
                    <TimePickerDialog
                        title={selectedEditable.label}
                        description={selectedEditable.description}
                        open={showEditableDialog}
                        onConfirm={e => withUnsavedChanges(updateTimeValue(e))}
                        onReset={() => withUnsavedChanges(resetTimeValue())}
                        onClose={deselectEditable}
                        onChange={e => {
                            handleChange(e, updateTimeValue)
                        }}
                        currentTime={selectedEditable.DOMElement.innerText}
                        timeFormat={
                            selectedEditable?.inputCustomFormat ??
                            defaultDisplayTimeFormat
                        }
                        resetAndFormatFixEnabled={
                            isTimePickerResetAndFormatFlagEnabled
                        }
                    />
                )
            case InputTypes.Dropdown:
                return (
                    <DropdownDialog
                        title={selectedEditable.label}
                        description={selectedEditable.description}
                        open={showEditableDialog}
                        onConfirm={e =>
                            withUnsavedChanges(updateDropdownValue(e))
                        }
                        onReset={() => withUnsavedChanges(resetDropdownValue())}
                        onClose={deselectEditable}
                        currentValue={selectedEditable.DOMElement.innerText}
                        dropdownItems={selectedEditable.options}
                        onChange={e => {
                            handleChange(e, updateDropdownValue)
                        }}
                    />
                )
            case InputTypes.HtmlEditor:
                return (
                    <HtmlDialog
                        title={selectedEditable.label}
                        description={selectedEditable.description}
                        open={showEditableDialog}
                        currentHtml={htmlEditorState[selectedEditable.id]}
                        onConfirm={e =>
                            withUnsavedChanges(updateHtmlEditorState(e))
                        }
                        onReset={() =>
                            withUnsavedChanges(resetHtmlEditorState())
                        }
                        onClose={deselectEditable}
                        fontSize={documentData.fontSize}
                        fontFamily={documentData.fontFamily}
                        onChange={e => {
                            handleChange(e, updateHtmlEditorState)
                        }}
                        isMandatory={selectedEditable.isMandatory}
                    />
                )
            case InputTypes.Carousel:
                return (
                    <CarouselDialog
                        title={selectedEditable.label}
                        description={selectedEditable.description}
                        open={showEditableDialog}
                        onConfirm={(id, html) =>
                            withUnsavedChanges(updateCarouselValue(id, html))
                        }
                        onReset={() => withUnsavedChanges(resetCarouselValue())}
                        onClose={deselectEditable}
                        currentIndex={carouselState[selectedEditable.id]}
                        carouselOptions={selectedEditable.options}
                        fontSize={documentData.fontSize}
                        fontFamily={documentData.fontFamily}
                        currentHtmlText={selectedEditable.DOMElement.innerHTML}
                    />
                )
            default:
                throw new Error('Editable using un-recognised input type id')
        }
    }

    const ConfirmationDialog = () => {
        return (
            <Dialog
                disableBackdropClick
                disableEscapeKeyDown
                maxWidth='xs'
                aria-labelledby='confirmation-dialog-title'
                open={openReviewModal}
            >
                <DialogTitle id='confirmation-dialog-title'>
                    Send For Review
                </DialogTitle>
                <DialogContent dividers>
                    If you send this document for review you will lose the
                    ability to edit it until the review has been completed. Are
                    you sure you want to submit it now?
                </DialogContent>
                <DialogActions>
                    <Button
                        autoFocus
                        onClick={handleCancelReviewModal}
                        color='primary'
                    >
                        Cancel
                    </Button>
                    <Button onClick={setInReview} color='secondary'>
                        Send
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    const handleCancelReviewModal = () => {
        setOpenReviewModal(false)
    }

    const CompletionConfirmationModal = () => {
        return (
            <Dialog
                disableBackdropClick
                disableEscapeKeyDown
                maxWidth='xs'
                aria-labelledby='confirmation-dialog-title'
                open={completionConfirmationModal}
            >
                <DialogTitle id='confirmation-dialog-title'>
                    Complete Document
                </DialogTitle>
                <DialogContent dividers>
                    Once a document is complete it cannot be edited or re-issued
                </DialogContent>
                <DialogActions>
                    <Button
                        autoFocus
                        onClick={handleCancelCompletionModal}
                        color='primary'
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => completeDocument()}
                        color='secondary'
                    >
                        Complete
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    const handleCancelCompletionModal = () => {
        setCompletionConfirmationModal(false)
    }

    const reviewDocument = async () => {
        setOpenReviewModal(false)
        const pagesContainer = document.getElementById('pages')
        try {
            await wizardService.reviewDocument({
                documentGuid: documentData.documentGuid,
                contentHtml: getDocumentHTML(pagesContainer),
            })
            dispatch(
                enqueueSnackbar({
                    message: 'Document sent for review',
                    options: {
                        variant: 'success',
                    },
                })
            )
            returnToUrl()
        } catch (error) {
            dispatch(
                enqueueSnackbar({
                    message:
                        'Could not send document for review. Please try again.',
                    options: {
                        variant: 'error',
                    },
                })
            )
        }
    }

    const endReview = async () => {
        setDocumentStatus(DocumentStatus.ReviewEnded)
        endReviewDocument().then(() => {
            saveDocument({
                returnAfterSave: true,
                status: DocumentStatus.ReviewEnded,
            })
        })
    }

    const endReviewDocument = async () => {
        const pagesContainer = document.getElementById('pages')

        try {
            setIsSaving(true)

            await wizardService.endReviewDocument({
                documentGuid: documentData.documentGuid,
                contentHtml: getDocumentHTML(pagesContainer),
            })

            setUnsavedChanges(false)
            setIsSaving(false)
            returnToUrl()
        } catch (error) {
            setIsSaving(false)
            dispatch(
                enqueueSnackbar({
                    message: 'Could not save document.',
                    options: {
                        variant: 'error',
                    },
                })
            )
        }
    }

    const setInReview = async () => {
        setDocumentStatus(DocumentStatus.AdviserReview)
        await reviewDocument().then(() => {
            saveDocument({
                returnAfterSave: true,
                status: DocumentStatus.AdviserReview,
            })
        })
    }

    const completeDocument = async () => {
        setCompletionConfirmationModal(false)
        removeNonMandatoryFieldsOnCompletion()

        const pagesContainer = document.getElementById('pages')
        const parsedQuery = queryString.parse(location.search)
        let {
            caseId,
            templateId,
            eventSetupId,
            workflowComponentId,
            workflowVersion,
            workflowTrackingComponentId,
        } = parsedQuery

        try {
            caseId = isDocDirectRoute ? caseIdForDocServiceuser : caseId

            setIsCompleting(true)
            const { isCompleted } = await wizardService.completeDocument({
                documentGuid: documentData.documentGuid,
                contentHtml: getDocumentHTML(pagesContainer),
                caseId: caseId,
                templateId: templateId,
                eventSetupId: eventSetupId,
                workflowComponentId: workflowComponentId,
                workflowVersion: workflowVersion,
                workflowTrackingComponentId: workflowTrackingComponentId,
            })
            if (isCompleted) {
                setDocumentStatus(DocumentStatus.Completed)
                setUnsavedChanges(false)
                returnToUrl()
            }
            dispatch(
                enqueueSnackbar({
                    message: 'Completed document',
                    options: {
                        variant: 'success',
                    },
                })
            )
            setIsCompleting(false)
        } catch (error) {
            setIsCompleting(false)
            dispatch(
                enqueueSnackbar({
                    message: 'Could not complete document. Please try again.',
                    options: {
                        variant: 'error',
                    },
                })
            )
        }
    }

    const goBack = () => {
        if (!unsavedChanges) {
            returnToUrl()
            return
        }

        setIsConfirmExitModalOpen(true)
    }

    const silentSaving = async () => {
        const pagesContainer = document.getElementById('pages')
        const parsedQuery = queryString.parse(location.search)

        const {
            caseId,
            eventSetupId,
            workflowComponentId,
            workflowVersion,
        } = parsedQuery
        var result = await wizardService.saveDocument({
            documentGuid: documentData.documentGuid,
            contentHtml: getDocumentHTML(pagesContainer),
            status: documentStatus,
            inReviewWithChanges: managerMadeChanges,
            caseId: caseId,
            eventSetupId: eventSetupId,
            workflowComponentId: workflowComponentId,
            workflowVersion: workflowVersion,
        })

        dispatch(
            enqueueSnackbar({
                message: 'Document was auto saved',
                options: {
                    variant: 'success',
                },
            })
        )

        tempSavedDateRef.current = result.documentVersionLastSavedDate
    }

    const saveDocument = useCallback(
        async ({ returnAfterSave, status, autoSaveDocument }) => {
            const pagesContainer = document.getElementById('pages')
            const parsedQuery = queryString.parse(location.search)
            let {
                caseId,
                templateId,
                eventSetupId,
                workflowComponentId,
                workflowVersion,
                workflowTrackingComponentId,
            } = parsedQuery

            caseId = isDocDirectRoute ? caseIdForDocServiceuser : caseId

            try {
                setIsSaving(true)
                var result = await wizardService.saveDocument({
                    documentGuid: documentData.documentGuid,
                    contentHtml: getDocumentHTML(pagesContainer),
                    status: status || documentStatus,
                    inReviewWithChanges: managerMadeChanges,
                    caseId: caseId,
                    templateId: templateId,
                    eventSetupId: eventSetupId,
                    workflowComponentId: workflowComponentId,
                    workflowVersion: workflowVersion,
                    workflowTrackingComponentId: workflowTrackingComponentId,
                })
                setLastAutoSavedDate(result.documentVersionLastSavedDate)
                setUnsavedChanges(false)
                setIsSaving(false)

                dispatch(
                    enqueueSnackbar({
                        message: autoSaveDocument
                            ? 'Document was auto saved'
                            : 'Saved document',
                        options: {
                            variant: 'success',
                        },
                    })
                )

                if (returnAfterSave) {
                    returnToUrl()
                }
            } catch (error) {
                setIsSaving(false)
                setUnsavedChanges(false)
                dispatch(
                    enqueueSnackbar({
                        message: 'Could not save document.',
                        options: {
                            variant: 'error',
                        },
                    })
                )
            }
        }
    )

    const handleEditClick = () => {
        setIsEditing(prevState => !prevState)
        if (!isEditing) {
            const pagesContainer = document.getElementById('pages')
            setEditableContent(getDocumentHTML(pagesContainer) || '')
        }
    }

    const handleSaveOfFullEdit = newContent => {
        const cleanedContent = removeEditableAttributes(newContent)

        setDocumentData(prevData => ({
            ...prevData,
            contentHtml: cleanedContent,
        }))
        setIsEditing(false)
        setUnsavedChanges(true)

        refreshPagesContent(cleanedContent)
    }

    const refreshPagesContent = contentHtml => {
        if (pagesRef.current) {
            pagesRef.current.innerHTML = ''

            // Set new content
            setupPages({
                content: contentHtml,
                header: documentData.branding?.headerHtml || '',
                footer: documentData.branding?.footerHtml || '',
            })

            addEditableClickHandlers(
                getAllEditables(),
                editableLookups,
                selectEditable
            )
        }
    }

    const downloadDocument = async () => {
        try {
            setIsDownloading(true)
            wizardService.getPdf(documentData.documentGuid).then(response => {
                downloadFile(response)
                setIsDownloading(false)
            })
        } catch (error) {
            dispatch(
                enqueueSnackbar({
                    message: 'Could not download preview. Please try again.',
                    options: {
                        variant: 'error',
                    },
                })
            )
            setIsDownloading(false)
        }
    }

    const previewDocument = async () => {
        const pagesContainer = document.getElementById('pages')
        try {
            setIsDownloading(true)
            wizardService
                .getPdfPreview({
                    documentGuid: documentData.documentGuid,
                    documentHtml: getDocumentHTML(pagesContainer),
                })
                .then(response => {
                    downloadFile(response)
                    setIsDownloading(false)
                })
        } catch (error) {
            dispatch(
                enqueueSnackbar({
                    message: 'Could not download preview. Please try again.',
                    options: {
                        variant: 'error',
                    },
                })
            )
            setIsDownloading(false)
        }
    }

    const [isSendToManagerOpen, setIsSendToManagerOpen] = useState(false)

    const handleSendToManagerClick = () => {
        setIsSendToManagerOpen(true)
    }

    const handleCloseSendToManager = () => {
        setIsSendToManagerOpen(false)
    }

    const handleCompleteClick = () => {
        setCompletionConfirmationModal(true)
    }

    const handleSendForReviewClick = () => {
        setOpenReviewModal(true)
    }

    const rightSubNav = () => {
        return (
            <WizardNavButtons
                location={location}
                documentStatus={documentStatus}
                documentData={documentData}
                progress={progress}
                lastAutoSavedDate={lastAutoSavedDate}
                isSaving={isSaving}
                isCompleting={isCompleting}
                isDownloading={isDownloading}
                isSendToManagerOpen={isSendToManagerOpen}
                showSendToManagerButton={isDisplaySendToManagerButton}
                documentCompleted={documentCompleted}
                documentInReview={documentInReview}
                documentReviewEnded={documentReviewEnded}
                documentInProgress={documentInProgress}
                isAdviser={isAdviser}
                isHrUser={isHrUser}
                hasHrUserRole={hasHrUserRole}
                reviewType={reviewType}
                caseParticipants={caseParticipants}
                caseId={caseId}
                userCanEditFullDocument={userCanEditFullDocument()}
                canComplete={canComplete()}
                docIsAdviserDependant={docIsAdviserDependant()}
                handleEditClick={handleEditClick}
                saveDocument={saveDocument}
                setInReview={setInReview}
                endReview={endReview}
                previewDocument={previewDocument}
                downloadDocument={downloadDocument}
                onSendToManagerClick={handleSendToManagerClick}
                onCloseSendToManager={handleCloseSendToManager}
                onCompleteClick={handleCompleteClick}
                onSendForReviewClick={handleSendForReviewClick}
                classes={classes}
            />
        )
    }

    return (
        <MainLayout
            rightSubNav={rightSubNav}
            progressPercentage={progress}
            onBackClick={goBack}
        >
            <Container className={classes.contentContainer}>
                {(!documentInReview || (isAdviser && documentInReview)) &&
                    selectedEditable &&
                    !documentCompleted && <PopoutComponent />}
                {(isBusinessUser || !isAdviser || isDocServiceUser) &&
                    documentInReview && (
                        <p className={classes.previewDisclaimer}>
                            <small>
                                <em>
                                    Document is in review, editing is disabled
                                </em>
                            </small>
                        </p>
                    )}

                {isDisplaySendToManagerButton && documentInReview && (
                    <>
                        <br />
                        <p className={classes.previewDisclaimer}>
                            <small>
                                <em>
                                    Document is in review, "Send to manager"
                                    button is disabled
                                </em>
                            </small>
                        </p>
                    </>
                )}

                <div
                    className={
                        !Object.keys(documentData)?.length ? classes.hide : null
                    }
                >
                    <h1>{documentData?.documentName || ''}</h1>

                    {/* Hidden announcement for screen readers */}
                    <div
                        role='status'
                        aria-live='assertive'
                        aria-atomic='true'
                        style={{
                            position: 'absolute',
                            left: '-9999px',
                            width: '1px',
                            height: '1px',
                            overflow: 'hidden',
                        }}
                    >
                        {titleAnnouncement}
                        {bodyAnnouncement}
                    </div>

                    {isEditing ? (
                        <HtmlDialog
                            title='Edit Document Content'
                            open={true}
                            currentHtml={editableContent}
                            onConfirm={handleSaveOfFullEdit}
                            onReset={() => setIsEditing(false)}
                            onClose={handleEditClick}
                            fontSize={documentData.fontSize}
                            fontFamily={documentData.fontFamily}
                        />
                    ) : (
                        <></>
                    )}
                    <div
                        className={classes.pages}
                        ref={pagesRefCallback}
                        id='pages'
                    ></div>
                </div>
                <div
                    className={
                        Object.keys(documentData).length ? classes.hide : null
                    }
                >
                    <LoadingPage text='Loading Document' />
                </div>
            </Container>
            <ScrollToTop />
            <Dialog
                disableBackdropClick={true}
                disableEscapeKeyDown={true}
                open={isConfirmExitModalOpen}
                onClose={() => setIsConfirmExitModalOpen(false)}
                aria-labelledby='alert-dialog-title'
                aria-describedby='alert-dialog-description'
            >
                <DialogTitle id='alert-dialog-title'>
                    {'Unsaved Changes'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id='alert-dialog-description'>
                        You have unsaved changes. Are you sure you wish to exit
                        without saving them?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        disabled={isSaving}
                        onClick={() => setIsConfirmExitModalOpen(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        disabled={isSaving}
                        onClick={returnToUrl}
                        variant='outlined'
                        color='secondary'
                    >
                        Exit without saving
                    </Button>
                    <div className={classes.modalSaveButtonWrapper}>
                        <Button
                            disabled={isSaving}
                            onClick={() =>
                                saveDocument({ returnAfterSave: true })
                            }
                            variant='contained'
                            color='secondary'
                        >
                            Save &amp; Exit
                        </Button>
                        {isSaving && (
                            <CircularProgress
                                size={24}
                                className={classes.modalSaveButtonProgress}
                            />
                        )}
                    </div>
                </DialogActions>
            </Dialog>
            <ConfirmationDialog />
            <CompletionConfirmationModal />
        </MainLayout>
    )

    function canComplete() {
        return (
            (reviewType === reviewTypeConstants.AdviserCompletion &&
                isAdviser) ||
            (reviewType !== reviewTypeConstants.AdviserCompletion &&
                !managerMadeChanges)
        )
    }

    function docIsAdviserDependant() {
        return (
            reviewType === reviewTypeConstants.AdviserReview ||
            reviewType === reviewTypeConstants.AdviserCompletion
        )
    }
}

export default Wizard
